import React, { useContext, useEffect, useMemo, useState } from "react";
import { PlusCircle, RefreshCw, Search } from "lucide-react";
import { toast } from "react-toastify";
import useTickets from "../../services/Support/useTickets";

import ClientTicketDrawer from "./ClientTicketDrawer";
import RaiseTicketModal from "./RaiseTicketModal";
import ClientTicketCard from "./ClientTicketCard";
import { GlobalContext } from "../../Context/GlobalContext";
import "./ClientTicket.css";
import NoIndex from "../../components/NoIndex";
import LoadingScreen from "../../utils/LoadingScreen";
import { formatDateTime } from "../../utils/dateFormatter";

const ClientSupportTickets = () => {
  const { fetchTickets, loading } = useTickets();
  const { userID } = useContext(GlobalContext);

  const [tickets, setTickets] = useState([]);
  const [q, setQ] = useState("");

  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);

  const [openModal, setOpenModal] = useState(false);

  // ---------------- FETCH ----------------

  const loadTickets = async () => {
    const res = await fetchTickets();

    if (res?.responseCode === "00") {
      setTickets(res.response || []);
    } else {
      toast.error(res?.responseMessage || "Failed to load tickets");
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  // ---------------- SEARCH ----------------

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tickets;

    return tickets.filter(
      (t) =>
        (t.ticket_code || "").toLowerCase().includes(s) ||
        (t.subject || "").toLowerCase().includes(s) ||
        (t.description || "").toLowerCase().includes(s) ||
        (t.department || "").toLowerCase().includes(s) ||
        (t.priority || "").toLowerCase().includes(s) ||
        (t.status || "").toLowerCase().includes(s),
    );
  }, [tickets, q]);

  const openDrawerFor = (t) => {
    setActiveTicket(t);
    setOpenDrawer(true);
  };

  return (
    <>
      <NoIndex />
      <div className="p-6 h-full min-h-0 flex flex-col overflow-hidden montserrat-font-1">
        <div className="sticky top-0 z-30 bg-[#dfedff36] pb-6">
        {/* HEADER */}
        <div className="flex justify-between items-start bg-purple-400 text-white p-5 rounded-lg montserrat-font-1">
          <div>
            <h2 className="text-xl font-semibold montserrat-font-1">Support & Help</h2>

            <p className="text-sm text-gray-200 montserrat-font-2">
              Track issues, chat with support, and raise tickets.
            </p>
          </div>

          <div className="flex gap-3 montserrat-font-1">
            <button
              onClick={() => loadTickets()}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              onClick={() => setOpenModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <PlusCircle size={18} />
              Raise Ticket
            </button>
          </div>
        </div>
        </div>

        {/* SEARCH */}
        <div className="flex items-center border rounded-md px-3 py-2 mb-5 gap-2 bg-white montserrat-font-1">
          <Search size={18} className="text-gray-400" />

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tickets..."
            className="w-full outline-none"
          />
        </div>

        {/* GRID */}
       <div className="flex-1 min-h-0 overflow-auto">
  {loading ? (
          <div className="flex justify-center py-6">
            <LoadingScreen text="Updating....." />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex justify-center montserrat-font-1">
            <div className="bg-white p-10 rounded-lg shadow text-center">
              <h4 className="font-semibold mb-2">No tickets found</h4>

              <p className="text-gray-500 mb-4 montserrat-font-2">
                Create a new support ticket if you need help.
              </p>

              <button
                onClick={() => setOpenModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Raise Ticket
              </button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 montserrat-font-1">
            {filtered.map((t) => (
              <ClientTicketCard
                key={t.pk_ticket_id}
                ticket={t}
                onOpen={() => openDrawerFor(t)}
              />
            ))}

            {/* CREATE CARD */}
            <div
              onClick={() => setOpenModal(true)}
              className="border rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer bg-purple-100 montserrat-font-1"
            >
              <PlusCircle size={32} />
              <p className="font-semibold mt-2">Raise New Ticket</p>
              <span className="text-sm text-gray-500 montserrat-font-2">
                Describe your issue and we’ll get back to you quickly.
              </span>
              <p className="font-semibold mt-2 underline">Raise New Ticket</p>
            </div>
          </div>
        )}

        {/* DRAWER */}
        <ClientTicketDrawer
          open={openDrawer}
          ticket={activeTicket}
          userID={userID}
          onClose={(refetch) => {
            setOpenDrawer(false);
            setActiveTicket(null);
            if (refetch) loadTickets(true);
          }}
        />

        {/* MODAL */}
        <RaiseTicketModal
          open={openModal}
          userID={userID}
          onClose={(didCreate) => {
            setOpenModal(false);
            if (didCreate) loadTickets(true);
          }}
        />
      </div>
      </div>
    </>
  );
};

export default ClientSupportTickets;
